import { useQuery } from '@apollo/client';
import {
  LoadingLayout,
  PageLayout,
  Separator,
  Text,
} from '../../../components';
import { TodosResponse } from '../../../types';
import { TaskDetailsDialog } from '../components';
import { TodoContainerStyle } from '../styles';
import { useState } from 'react';
import { GET_TODO_SUB_SOURCES, GET_TODOS } from '@repo-sapiens/graphql';
import { T_ActionItems } from '@repo-sapiens/data-access-models';
import { css } from '@emotion/css';
import { FontFamily, theme } from '@repo-sapiens/theme';
import { format } from 'date-fns';
import { FilterItem } from '../components/FilterItem';

type TodoFilter = {
  type?: 'Commitment' | 'Request';
  priority?: 'High' | 'Medium' | 'Low';
  sourceScope?: 'Internal' | 'External' | 'Unknown';
  subsource?: string;
};

export const TodoScreen = () => {
  const [filter, setFilter] = useState({} as TodoFilter);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const { data: todoData, loading } = useQuery<TodosResponse>(GET_TODOS, {
    variables: {
      page,
      limit,
      filter,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const { data: subSourceData } = useQuery<{ distinctSubSources?: string[] }>(
    GET_TODO_SUB_SOURCES
  );

  const onFilterChange = (key: keyof TodoFilter, value: string) => {
    setFilter((prev) => ({
      ...prev,
      [key]: value,
    }));
  };
  const clearOneFilter = (key: keyof TodoFilter) => {
    setFilter((prev) => ({
      ...prev,
      [key]: undefined,
    }));
  };
  const clearAllFilters = () => {
    setFilter({} as TodoFilter);
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<T_ActionItems | null>(null);

  const handleSelect = (task: T_ActionItems) => {
    setSelectedTask(task);
    setIsModalOpen(true);
    console.log('selectedTask -->', task);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTask(null);
  };

  const todos = todoData?.actionItems || [];
  return (
    <PageLayout title="Action Items | Multiplayer">
      <div className={TodoContainerStyle}>
        {todos?.length > 0 && <Text.H3>In case you missed these!</Text.H3>}
        <Separator.Horizontal spacing={8} />
        <div
          className={css`
            display: flex;
            flex-direction: row;
            align-items: center;
          `}
        >
          <Text.Body2XS fontFamily={FontFamily.RoobertLight} color="seconday">
            Filter by:
          </Text.Body2XS>
          <Separator.Vertical spacing={2} />
          <FilterItem
            title="Type"
            options={['Commitment', 'Request']}
            selected={filter.type}
            onSelect={(value) => onFilterChange('type', value)}
            onClear={() => clearOneFilter('type')}
          />
          <Separator.Vertical spacing={1} />
          <FilterItem
            title="Priority"
            options={['High', 'Medium', 'Low']}
            selected={filter.priority}
            onSelect={(value) => onFilterChange('priority', value)}
            onClear={() => clearOneFilter('priority')}
          />
          <Separator.Vertical spacing={1} />
          <FilterItem
            title="Source"
            options={['Internal', 'External', 'Unknown']}
            selected={filter.sourceScope}
            onSelect={(value) => onFilterChange('sourceScope', value)}
            onClear={() => clearOneFilter('sourceScope')}
          />
          <Separator.Vertical spacing={1} />
          <FilterItem
            title="Profile"
            options={subSourceData?.distinctSubSources || []}
            selected={filter.subsource}
            onSelect={(value) => onFilterChange('subsource', value)}
            onClear={() => clearOneFilter('subsource')}
          />
          <Separator.Vertical spacing={2.5} />
          <Text.Body2XS
            fontFamily={FontFamily.RoobertLight}
            onClick={clearAllFilters}
            style={{ cursor: 'pointer' }}
            color="seconday"
          >
            Clear all
          </Text.Body2XS>
        </div>
        <Separator.Horizontal spacing={4} />
        {loading && <LoadingLayout />}
        {!loading && todos?.length === 0 && (
          <Text.Body>
            Nothing to see here! Your assistant is on a break.
          </Text.Body>
        )}
        <table
          className={css`
            border-collapse: collapse;
            width: 100%;
            td {
              border-bottom: 1px solid ${theme.colors.border.primary};
              padding: 4px 0px;
            }
            tr {
              cursor: pointer;
            }
          `}
        >
          <tbody>
            {todos?.map((todo) => (
              <tr key={todo._id} onClick={() => handleSelect(todo)}>
                <td
                  className={css`
                    padding-right: 24px !important;
                  `}
                >
                  <Text.Body2XS fontFamily={FontFamily.RoobertLight}>
                    <span
                      className={css`
                        color: ${theme.colors.text['highlight/3']};
                      `}
                    >
                      From {todo.source}:
                    </span>{' '}
                    {todo.task}
                  </Text.Body2XS>
                </td>
                <td
                  className={css`
                    width: 160px;
                    text-align: right;
                  `}
                >
                  <Text.Body2XS fontFamily={FontFamily.RoobertLight}>
                    <span>{todo.subsource || 'Unknown'}</span> •{' '}
                    <span>
                      {todo.dueDate
                        ? format(new Date(todo.dueDate), 'EEEE, MMMM do')
                        : 'Unspecified'}
                    </span>
                  </Text.Body2XS>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <TaskDetailsDialog
          open={isModalOpen}
          onOpenChange={handleCloseModal}
          item={selectedTask || undefined}
        />
      </div>
    </PageLayout>
  );
};
